/* @flow */
import React from 'react';
import gql from 'graphql-tag';
import classesSummaryFragment from 'groups/fragments/classesSummaryFragment.gql';
import BoxBody from 'components/BoxBody.jsx';
import TitleHeader from 'components/TitleHeader.jsx';
import GradeLevelPicker from 'components/GradeLevelPicker.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupName from 'groups/components/GroupName.jsx';
import LoadingModal from 'components/LoadingModal.jsx';
import SchoolPicker from 'components/SchoolPicker.jsx';
import SubjectPicker from 'components/SubjectPicker.jsx';
import AffiliationStore from 'stores/AffiliationStore';
import ToastActions from 'actions/ToastActions';
import Text from 'components/Text.jsx';
import { UNAFFILIATED } from 'constants/OrganizationConstants';
import { graphql } from 'react-apollo';

import './styles/TeacherInfoCollection.scss';

const SCREEN_NAME = 'teacher_info_collection';

type Props = {
  groups: Object[],
  loading: boolean,
};

export class TeacherInfoCollection extends React.Component {
  props: Props;

  static childContextTypes = {
    eventScreenName: React.PropTypes.string,
  };

  getChildContext() {
    return {
      eventScreenName: SCREEN_NAME,
    };
  }

  selectSchool(group: Group, organizationId: ?number) {
    GroupActions.patch(group, { organization_id: organizationId })
      .catch(err => {
        ToastActions.showAPIError(err);
        throw err;
      });
  }

  saveGradeLevels(group: Group, gradeLevels: string[]) {
    GroupActions.patch(group, { grades: gradeLevels })
      .catch(err => {
        ToastActions.showAPIError(err);
        throw err;
      });
  }

  saveSubject(group: Group, subject: ?string) {
    GroupActions.patch(group, { subject })
      .catch(err => {
        ToastActions.showAPIError(err);
        throw err;
      });
  }

  renderTableHeader() {
    return (
      <tr>
        <th>Class</th>
        <th>Class is at</th>
        <th>Grade(s)</th>
        <th>Subject</th>
      </tr>
    );
  }

  renderTableRow(group: Object) {
    const schoolSuggestions = AffiliationStore.getMemberOrganizations();
    const groupEntity = Group.fromJSON(group);
    if (!group.has_children) {
      schoolSuggestions.push(UNAFFILIATED);
    }
    return (
      <tr key={group.id}>
        <th>
          <GroupAvatar group={groupEntity} size="small" />
          <Text className="TeacherInfoCollection-groupName" truncate>
            <GroupName group={groupEntity} />
          </Text>
        </th>
        <th>
          <SchoolPicker
            onSelect={this.selectSchool.bind(this, groupEntity)}
            onClear={() => {}}
            suggestions={schoolSuggestions}
            schoolId={group.organization ? group.organization.id : undefined}
            selectedSchool={group.organization ? undefined : UNAFFILIATED}
          />
        </th>
        <th>
          <GradeLevelPicker
            onClose={this.saveGradeLevels.bind(this, groupEntity)}
            initialGrades={group.grades.map(g => g.code)}
          />
        </th>
        <th>
          <SubjectPicker
            onChange={this.saveSubject.bind(this, groupEntity)}
            initialSubject={group.subject && group.subject.subject_code}
          />
        </th>
      </tr>
    );
  }

  renderTable() {
    const groups = this.props.groups.map(group => this.renderTableRow(group));
    return (
      <table className="TeacherInfoCollection-table">
        <thead>{this.renderTableHeader()}</thead>
        <tbody>{groups}</tbody>
      </table>
    );
  }

  render() {
    if (this.props.loading) {
      return <LoadingModal />;
    }
    return (
      <BoxBody>
        <TitleHeader
          title="Update your class information"
          subtext="Make sure you have the correct school, grade and subject for each class"
        />
        {this.renderTable()}
      </BoxBody>
    );
  }
}

const withGroups = graphql(gql`
  ${classesSummaryFragment}

  query {
    me {
      id,
      ownedClasses: classes(classType: "owned") {
        ...classesSummary
        has_children
        organization {
          id
          name
        }
        subject {
          name
          subject_code
        }
        grades {
          name
          short_name
          code
        }
      }
    }
  }
  `,
  {
    props: ({ data }) => ({
      groups: data.me ? data.me.ownedClasses : [],
      loading: data.loading,
    }),
  }
);

export default withGroups(TeacherInfoCollection);




// WEBPACK FOOTER //
// ./src/user/components/TeacherInfoCollection.jsx